import { API_URL, FetchApi } from "@/services/service";
import User from "../models/User";

const API_BASE_URL = `${API_URL}/auth/`;

/**
 * Payload pour l'envoi d'un code OTP
 */
export interface SendOtpPayload {
    email: string;
}

/**
 * Payload pour la vérification d'un code OTP
 */
export interface VerifyOtpPayload {
    email: string;
    otp: string;
}

/**
 * Payload pour compléter l'inscription après OTP
 */
export interface CompleteRegistrationPayload {
    email: string;
    password: string;
    confirmed_password: string;
    firstname?: string;
    lastname?: string;
    phonenumber?: string;
}

/**
 * Payload pour ajouter un nouvel utilisateur
 */
export interface AddUserPayload {
    email: string;
    first_name: string;
    last_name: string;
    phonenumber?: string;
    centers_access?: Array<{
        id: number;
        permissions?: number[];
    }>;
}

/**
 * Payload pour la connexion
 */
export interface LoginPayload {
    username: string;
    password: string;
}

/**
 * AuthServices - Service d'authentification
 *
 * Utilise Token Authentication uniquement (Django Rest Framework)
 * - Pas de refresh token
 * - Le token est stocké dans localStorage
 * - Déconnexion automatique si token expiré (401)
 *
 * Endpoints disponibles:
 * - Inscription avec OTP (send-otp, verify-otp, complete-registration)
 * - Connexion/Déconnexion (login, logout)
 * - Gestion utilisateurs (add-user, update-user)
 * - Invitations (invitations, respond-invitation-email)
 * - Changement de mot de passe (change-password)
 */
export const AuthServices = {
    /**
     * Envoyer un code OTP par email
     * @param payload Email de l'utilisateur
     * @returns Promesse avec la réponse du serveur
     */
    sendOtp: (payload: SendOtpPayload) =>
        FetchApi.post(`${API_BASE_URL}send-otp/`, payload),

    /**
     * Vérifier le code OTP reçu par email
     * @param payload Email et code OTP
     * @returns Promesse avec la réponse du serveur
     */
    verifyOtp: (payload: VerifyOtpPayload) =>
        FetchApi.post(`${API_BASE_URL}verify-otp/`, payload),

    /**
     * Compléter l'inscription après vérification OTP
     * @param payload Informations d'inscription (email, mot de passe, etc.)
     * @returns Promesse avec la réponse du serveur
     */
    completeRegistration: (payload: CompleteRegistrationPayload) =>
        FetchApi.post(`${API_BASE_URL}complete-registration/`, payload),

    /**
     * Récupérer les invitations pour un token donné
     * @param token Token d'invitation
     * @returns Promesse avec la liste des invitations
     */
    getInvitations: (token: string) =>
        FetchApi.get(`${API_BASE_URL}invitations/?token=${token}`),

    /**
     * Récupérer la liste des rôles disponibles
     * @returns Promesse avec la liste des rôles
     */
    getRoles: () =>
        FetchApi.get(`${API_BASE_URL}roles/`),

    /**
     * Répondre à une invitation par email
     * @param payload Email, token, réponse (accept/reject), mot de passe
     * @returns Promesse avec la réponse du serveur
     */
    respondInvitationEmail: (payload: { email: string; token: string; answer: string; password: string; confirmed_password: string  }) =>
        FetchApi.post(`${API_BASE_URL}respond-invitation-email/`, payload),

    /**
     * Changer le mot de passe d'un utilisateur
     * @param payload Token, email, nouveau mot de passe
     * @returns Promesse avec la réponse du serveur
     */
    change_password: (payload: { token: string; email:string ; password: string; confirmed_password: string }) =>
        FetchApi.post(`${API_BASE_URL}change-password/`, payload),

    /**
     * Ajouter un nouvel utilisateur
     * @param payload Informations de l'utilisateur
     * @returns Promesse avec l'utilisateur créé
     */
    addUser: (payload: User) =>
        FetchApi.post(`${API_BASE_URL}add-user/`, payload),

    /**
     * Mettre à jour un utilisateur existant
     * @param id ID de l'utilisateur
     * @param payload Informations à mettre à jour
     * @returns Promesse avec l'utilisateur mis à jour
     */
    updateUser: (id: number, payload: User) =>
        FetchApi.post(`${API_BASE_URL}${id}/update-user/`, payload),

    /**
     * Connexion de l'utilisateur
     * Retourne un token d'authentification à stocker dans localStorage
     * @param payload Username et mot de passe
     * @returns Promesse avec { token: string, user: User }
     */
    login: (payload: LoginPayload) =>
        FetchApi.post(`${API_BASE_URL}login/`, payload),

    /**
     * Récupérer les informations de l'utilisateur connecté
     * @param token Token d'authentification
     * @returns Promesse avec les informations utilisateur
     */
    getUserInformations: (token: string) =>
        FetchApi.get(`${API_BASE_URL}user-informations/`, token),

    /**
     * Déconnexion de l'utilisateur
     * Invalide le token côté serveur
     * @returns Promesse avec la réponse du serveur
     */
    logout: () =>
        FetchApi.post(`${API_BASE_URL}logout/`),
};